import { Component, OnInit } from "@angular/core";
import { StatsService } from "@/_services/stats.service";
import { debounceTime, Observable } from "rxjs";
import { shareReplay, tap, map, startWith } from "rxjs/operators";
import {
    buildPastDaysDateRange,
    reapplyTimeFrameRange,
} from "@/_utility/date.utils";
import { StatsAssuranceResult } from "@/_models/stats-assurance.interface";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { FilterService } from "@/_services/filter.service";
import { LOCAL_STORAGE_KEYS } from "@/_models/filters/local-storage-keys";
import { RadioOption } from "@/components/shared/radio-list/radio-list.component";
import {
    EventClassification,
    monitoringMethods,
    StatusState,
} from "@/_models/issue.interface";
import { EventClassificationService } from "@/_services/event-classification.service";
import { timeFrameListValues } from "@/components/safety-assurance/issue-list/issue-list.component";

@UntilDestroy()
@Component({
    selector: "app-performance-assurance",
    templateUrl: "./performance-assurance.component.html",
    styleUrls: ["./performance-assurance.component.scss"],
})
export class PerformanceAssuranceComponent implements OnInit {
    stats$: Observable<StatsAssuranceResult>;
    filterForm: UntypedFormGroup;
    statusList = StatusState;
    timeFrameList: RadioOption[] = timeFrameListValues;
    monitoringMethodList = monitoringMethods.map((method) => ({
        label: method.title,
        value: method.id,
    }));
    eventClassifications$: Observable<EventClassification[]>;
    currentFilters;

    constructor(
        private statsService: StatsService,
        private filterService: FilterService,
        private formBuilder: UntypedFormBuilder,
        private eventClassificationService: EventClassificationService,
    ) {}

    ngOnInit(): void {
        this.buildFilterForm();

        this.eventClassifications$ = this.eventClassificationService
            .getEventClassificationByAccount()
            .pipe(
                map((config) => config.classifications),
                shareReplay(),
            );
    }

    buildFilterForm() {
        const [defaultStart, defaultEnd] = buildPastDaysDateRange(-30);
        const initialFilters = {
            status: [],
            timeFrame: -30,
            createdAt: [defaultStart, defaultEnd],
            eventClassifications: [],
            FAA: "",
        };

        const savedFilters = this.filterService.loadSimpleFilters(
            LOCAL_STORAGE_KEYS.ASSURANCE_FILTERS,
        );

        const filters = savedFilters
            ? { ...initialFilters, ...savedFilters }
            : initialFilters;

        this.filterForm = this.formBuilder.group({
            status: [filters.status],
            timeFrame: [filters.timeFrame],
            createdAt: [filters.createdAt],
            eventClassifications: [filters.eventClassifications],
            FAA: [filters.FAA],
        });

        reapplyTimeFrameRange(this.filterForm, "createdAt");

        this.filterForm.valueChanges
            .pipe(
                startWith(this.filterForm.value),
                debounceTime(500),
                untilDestroyed(this),
                tap((changes) => {
                    this.currentFilters = { ...changes };
                    this.filterService.saveSimpleFilters(
                        LOCAL_STORAGE_KEYS.ASSURANCE_FILTERS,
                        { ...changes, status: [...changes.status] },
                    );
                }),
                tap(() => {
                    this.updateStatsData();
                }),
            )
            .subscribe();
    }

    updateStatsData() {
        this.stats$ = this.statsService
            .getAssuranceStats(this.filterForm.value)
            .pipe(shareReplay());
    }
}
